import React, {useState, useEffect} from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Brush, ReferenceLine,
} from 'recharts';
import styles from './RentaLineChart.module.scss';
import {CurveType} from "recharts/types/shape/Curve";
import {NoSwipeNavigation} from "@renta-apps/athenaeum-react-components";
import RentaEasyConstants from '@/helpers/RentaEasyConstants';

/**
 * Represents a legend item displayed in the chart.
 */
interface LegendItem {
    /**
     * The label for the legend item.
     * @example "Temperature"
     */
    label: string;

    /**
     * The color associated with the legend item.
     * @example "#FF0000"
     */
    color: string;
}

/**
 * Represents the data used in the chart.
 */
export interface ChartData {
    /**
     * The date associated with the data point.
     */
    date: Date;

    /**
     * The primary value of the data point.
     */
    value: number;

    /**
     * (Optional) The moving average value for the data point.
     */
    movingAverage?: number;
}

/**
 * Configuration for a line in the chart.
 */
interface LineConfig {
    /**
     * The key in the data array that this line represents.
     * @example "temperature"
     */
    dataKey: string;

    /**
     * The display name for the data represented by this line.
     * @example "Temperature"
     */
    dataName: string;

    /**
     * The stroke color for the line.
     * @example "#FF5733"
     */
    stroke: string;

    /**
     * (Optional) The width of the line stroke.
     * Defaults to `2` if not specified.
     */
    strokeWidth?: number;

    /**
     * (Optional) The ID of the gradient to be applied to the line stroke.
     * If provided, the line will use this gradient for its color.
     */
    gradientId?: string;

    /**
     * (Optional) The type of the line's curve.
     * Can be one of the curve types supported by Recharts.
     * @example "monotone"
     */
    type?: CurveType | undefined;
}

/**
 * Props for the reusable RentaLineChart component.
 */
interface ReusableLineChartProps {
    /**
     * The data to be displayed in the chart.
     */
    data: any[];

    /**
     * The key in the data array representing the x-axis values.
     */
    xKey: string;

    /**
     * Configuration for the lines to be drawn on the chart.
     */
    lines: LineConfig[];

    /**
     * (Optional) The title of the chart.
     */
    title?: string;

    /**
     * (Optional) The subtitle of the chart.
     */
    subtitle?: string;

    /**
     * (Optional) Custom legend items to be displayed.
     */
    legendItems?: LegendItem[];

    /**
     * (Optional) A formatter function for the x-axis labels.
     * @param value The raw x-axis value.
     * @returns A formatted x-axis label.
     */
    xAxisFormatter?: (value: string) => string;

    /**
     * (Optional) A formatter function for the tooltip.
     * @param value The value at the tooltip's data point.
     * @param name The name of the data series.
     * @param entry The entry object for the tooltip.
     * @returns A formatted tooltip string.
     */
    tooltipFormatter?: (value: number, name: string, entry: string) => string;

    /**
     * (Optional) Whether to display a brush for zooming in/out of the chart.
     * @default false
     */
    brush?: boolean;

    /**
     * (Optional) Additional class name for the chart container.
     */
    containerClassName?: string;

    /**
     * (Optional) The value at which to draw a reference line.
     */
    referenceLineValue?: number;

    /**
     * (Optional) The stroke color for the reference line.
     * @default "#8884d8"
     */
    referenceLineStroke?: string;
}

const RentaLineChart: React.FC<ReusableLineChartProps> = ({
    data,
    xKey,
    lines,
    title,
    subtitle,
    legendItems = [],
    xAxisFormatter,
    brush = false,
    containerClassName,
    referenceLineValue,
    referenceLineStroke,
}) => {
    return (
        <div className={`${styles.chartContainer} ${containerClassName}`} data-cy={"renta-line-chart-container"}>
            {/* Title Section */}
            {title && (
                <div className={styles.titleContainer}>
                    <div>
                        <h5 className={styles.title}>{title}</h5>
                        {subtitle && <div className={styles.subTitle}>{subtitle}</div>}
                    </div>
                    {/* Custom Legends */}
                    {legendItems.length > 0 && (
                        <div className={styles.legendItemContainer}>
                            {legendItems.map((item, index) => (
                                <div key={index} className={styles.legendItem}>
                                    <div>{item.label}</div>
                                    <div
                                        className={styles.ball}
                                        style={{backgroundColor: item.color}}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Chart Section */}
            <NoSwipeNavigation>
                <ResponsiveContainer width="100%"
                                     height={200}
                                     className={styles.chartContent}>
                    <LineChart data={data}
                               margin={{top: 20, right: 20, bottom: 5, left: 0}}
                    >
                        <defs>
                            {lines.map((line) =>
                                line.gradientId ? (
                                    <linearGradient key={line.gradientId}
                                                    id={line.gradientId}
                                                    x1="0"
                                                    y1="0"
                                                    x2="1"
                                                    y2="0"
                                    >
                                        <stop offset="0%" stopColor={line.stroke} stopOpacity={0}/>
                                        <stop offset="20%" stopColor={line.stroke} stopOpacity={1}/>
                                        <stop offset="100%" stopColor={line.stroke} stopOpacity={1}/>
                                    </linearGradient>
                                ) : null
                            )}
                        </defs>
                        <CartesianGrid className={styles.cartesianGrid}
                                       strokeDasharray="3 3"
                                       stroke="#e0e0e0"
                                       x1={220}
                                       vertical={false}
                        />

                        {referenceLineValue && (
                            <ReferenceLine ifOverflow="extendDomain"
                                           stroke-width={4}
                                           y={referenceLineValue}
                                           stroke={referenceLineStroke || RentaEasyConstants.Colors.lightPurple}
                                           strokeDasharray="3 3"
                            />
                        )

                        }
                        <XAxis dataKey={xKey}
                               axisLine={false}
                               tickMargin={0}
                               padding={{left: -20, right: 0}}
                               tickLine={false}
                               interval={"preserveStartEnd"}
                               minTickGap={38}
                               tick={{fontSize: 12}}
                               tickFormatter={xAxisFormatter}
                        />
                        <YAxis width={40}
                               tickFormatter={(value: any) => {
                                   //Don't show 0 on the y-axis labels
                                   if (value === 0) {
                                       return "";
                                   }
                                   return value;
                               }}
                               axisLine={false}
                               tickLine={false}
                               tick={{fontSize: 12, dx: 0}}
                        />
                        <Tooltip formatter={(value: number, name: string) => {
                            const lineConfig = lines.find((line) => line.dataKey === name);
                            const displayName = lineConfig?.dataName || name; // Use dataName if available
                            return [value.toFixed(2), displayName];
                        }}
                                 labelFormatter={(label: string) => {
                                     const date = new Date(label);
                                     return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
                                         .toString()
                                         .padStart(2, '0')}.${date.getFullYear()}`;
                                 }}
                        />
                        {lines.map((line) => (
                            <Line key={line.dataKey}
                                  type={line.type || 'linear'}
                                  dataKey={line.dataKey}
                                  stroke={line.gradientId ? `url(#${line.gradientId})` : line.stroke}
                                  strokeWidth={line.strokeWidth || 2}
                                  dot={false}
                            />
                        ))}
                        {brush && (
                            <Brush className={styles.brush}
                                   dataKey={xKey}
                                   height={8}
                                   padding={{top: 10}}
                                   travellerWidth={15}
                                   dx={5}
                                   dy={5}
                                   traveller={(props: any) => {
                                       const {x, y, width, height} = props;
                                       const ballSize = 10;
                                       const cx = x + width / 2;
                                       const cy = y + height / 2;
                                       const radius = Math.min(width + ballSize, height + ballSize) / 2;

                                       return (
                                           <circle cx={cx}
                                                   cy={cy}
                                                   r={radius}
                                                   stroke="none"
                                           />);
                                   }}
                                   tickFormatter={() => ""}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </NoSwipeNavigation>
        </div>
    );
};

export default RentaLineChart;